import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Fingerprint, 
  Search, 
  MapPin, 
  Briefcase, 
  Shield, 
  FileCheck, 
  Users, 
  CheckCircle,
  ArrowRight,
  Building2,
  GraduationCap,
  Plane,
  Heart,
  Scale,
  ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ServicesPage = () => {
  // Services that require fingerprinting
  const services = [
    {
      icon: <Briefcase className="w-8 h-8" />,
      title: "Employment Background Checks",
      description: "Required for many jobs including healthcare, education, government, and financial services.",
      examples: ["Teachers & School Staff", "Healthcare Workers", "Bank Employees", "Government Contractors"],
      color: "blue"
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Security Clearances",
      description: "FBI background checks for security-sensitive positions and access.",
      examples: ["Defense Contractors", "Intelligence Agencies", "TSA PreCheck", "TWIC Cards"],
      color: "purple"
    },
    {
      icon: <GraduationCap className="w-8 h-8" />,
      title: "Professional Licensing",
      description: "State licensing requirements for various professions.",
      examples: ["Attorneys", "Real Estate Agents", "Insurance Agents", "Notary Publics"],
      color: "green"
    },
    {
      icon: <Plane className="w-8 h-8" />,
      title: "Immigration & Visas",
      description: "USCIS requirements for immigration applications and status changes.",
      examples: ["Green Card Applications", "Citizenship", "Work Visas", "Adoption"],
      color: "orange"
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Volunteer & Foster Care",
      description: "Background checks for volunteers and foster/adoption parents.",
      examples: ["Foster Parents", "Adoption", "Youth Organizations", "School Volunteers"],
      color: "pink"
    },
    {
      icon: <Scale className="w-8 h-8" />,
      title: "Legal & Court Orders",
      description: "Court-ordered fingerprinting for various legal matters.",
      examples: ["Name Changes", "Expungements", "Record Sealing", "Court Cases"],
      color: "slate"
    }
  ];

  const colorMap = {
    blue: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', icon: 'text-blue-400', hover: 'hover:border-blue-500/50' },
    purple: { bg: 'bg-purple-500/10', border: 'border-purple-500/30', icon: 'text-purple-400', hover: 'hover:border-purple-500/50' },
    green: { bg: 'bg-green-500/10', border: 'border-green-500/30', icon: 'text-green-400', hover: 'hover:border-green-500/50' },
    orange: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', icon: 'text-orange-400', hover: 'hover:border-orange-500/50' },
    pink: { bg: 'bg-pink-500/10', border: 'border-pink-500/30', icon: 'text-pink-400', hover: 'hover:border-pink-500/50' },
    slate: { bg: 'bg-slate-500/10', border: 'border-slate-500/30', icon: 'text-slate-400', hover: 'hover:border-slate-500/50' },
  };

  return (
    <div className="min-h-screen bg-slate-900 relative" data-testid="services-page">
      {/* Background */}
      <div 
        className="fixed inset-0 z-0"
        style={{
          backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/07e51985-c374-4472-a4ec-7832a182bb43/images/a599bfb20d8b730453411fb0496e41df29ac3936a599b1146f16c28f94161081.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      />
      <div className="fixed inset-0 z-0 bg-slate-900/90" />
      
      {/* Content */}
      <div className="relative z-10">
        {/* Header */}
        <header className="border-b border-white/10 bg-slate-900/50 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <Fingerprint className="w-8 h-8 text-blue-400" />
              <span className="text-xl font-bold text-white">FingerprintVS</span>
            </Link>
            <nav className="flex items-center gap-6">
              <Link to="/services" className="text-blue-400 font-medium">Services</Link>
              <Link to="/find-provider" className="text-gray-300 hover:text-white transition">Find a Provider</Link>
              <Link to="/login" className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg font-medium transition">
                Provider Login
              </Link>
            </nav>
          </div>
        </header>

        {/* Hero Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Need Fingerprinting Services?
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Whether it's for a job, license, visa, or legal requirement — we connect you with 
              certified fingerprinting providers in your area.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/find-provider"
                className="px-8 py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-semibold text-lg transition flex items-center justify-center gap-2"
                data-testid="find-provider-btn"
              >
                <MapPin className="w-5 h-5" />
                Find a Provider Near Me
              </Link>
            </div>
          </div>
        </section>

        {/* What We Help With */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-white text-center mb-4">
              What We Help With
            </h2>
            <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">
              Our certified providers offer FBI-compliant fingerprinting services for a wide range of purposes.
            </p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {services.map((service, idx) => {
                const colors = colorMap[service.color];
                return (
                  <div 
                    key={idx}
                    className={`${colors.bg} ${colors.border} ${colors.hover} border rounded-xl p-6 transition`}
                  >
                    <div className={`${colors.icon} mb-4`}>
                      {service.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-white mb-2">{service.title}</h3>
                    <p className="text-gray-400 text-sm mb-4">{service.description}</p>
                    <ul className="space-y-1">
                      {service.examples.map((example, i) => (
                        <li key={i} className="text-gray-500 text-sm flex items-center gap-2">
                          <CheckCircle className="w-3 h-3 text-green-500" />
                          {example}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* What to Expect */}
        <section className="py-16 px-4 bg-white/5">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white text-center mb-12">
              What to Expect
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-blue-400">1</span>
                </div>
                <h3 className="text-white font-semibold mb-2">Find a Provider</h3>
                <p className="text-gray-400 text-sm">Search for a certified fingerprinting provider near you.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-blue-400">2</span>
                </div>
                <h3 className="text-white font-semibold mb-2">Schedule Appointment</h3>
                <p className="text-gray-400 text-sm">Contact the provider to schedule your appointment. Bring valid ID.</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-bold text-blue-400">3</span>
                </div>
                <h3 className="text-white font-semibold mb-2">Get Your Documents</h3>
                <p className="text-gray-400 text-sm">Receive your FBI-compliant fingerprint cards or electronic submission.</p>
              </div>
            </div>
          </div>
        </section>

        {/* What to Bring */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white text-center mb-8">
              What to Bring to Your Appointment
            </h2>
            
            <div className="bg-white/5 border border-white/10 rounded-xl p-8">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <FileCheck className="w-5 h-5 text-green-400" />
                    Required Documents
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3 text-gray-300">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Valid government-issued photo ID (Driver's License, Passport, State ID)</span>
                    </li>
                    <li className="flex items-start gap-3 text-gray-300">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Any forms or paperwork from your employer/agency</span>
                    </li>
                    <li className="flex items-start gap-3 text-gray-300">
                      <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span>Payment (cash, card - check with provider)</span>
                    </li>
                  </ul>
                </div>
                
                <div>
                  <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                    <Users className="w-5 h-5 text-blue-400" />
                    Good to Know
                  </h3>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3 text-gray-300">
                      <ChevronRight className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <span>Appointment takes about 15-30 minutes</span>
                    </li>
                    <li className="flex items-start gap-3 text-gray-300">
                      <ChevronRight className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <span>Avoid lotions or hand creams before appointment</span>
                    </li>
                    <li className="flex items-start gap-3 text-gray-300">
                      <ChevronRight className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <span>Cards are typically available same day</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-gray-400 mb-8">
              Find a certified FingerprintVS provider in your area today.
            </p>
            <Link 
              to="/find-provider"
              className="inline-flex items-center gap-2 px-8 py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-semibold text-lg transition"
            >
              <Search className="w-5 h-5" />
              Find a Provider
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>

        {/* Are You a Business? */}
        <section className="py-16 px-4 bg-gradient-to-r from-purple-900/30 to-blue-900/30 border-t border-white/10">
          <div className="max-w-4xl mx-auto text-center">
            <Building2 className="w-12 h-12 text-purple-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-4">
              Are You a Fingerprinting Service Provider?
            </h2>
            <p className="text-gray-400 mb-6">
              Join our network of certified providers and grow your business with FingerprintVS.
            </p>
            <Link 
              to="/for-vendors"
              className="inline-flex items-center gap-2 px-6 py-3 bg-purple-500 hover:bg-purple-600 text-white rounded-lg font-semibold transition"
            >
              Learn About Our Platform
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-8 px-4 border-t border-white/10">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Fingerprint className="w-6 h-6 text-blue-400" />
              <span className="text-white font-semibold">FingerprintVS</span>
              <span className="text-gray-500 text-sm">• Fingerprinting Virtual System</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-gray-400">
              <Link to="/services" className="hover:text-white transition">Services</Link>
              <Link to="/find-provider" className="hover:text-white transition">Find Provider</Link>
              <Link to="/for-vendors" className="hover:text-white transition">For Vendors</Link>
              <a href="mailto:support@fingerprintvs.com" className="hover:text-white transition">Contact</a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default ServicesPage;
